import toolkit from '@/app/mixins/toolkit/item';
import pension from '@/app/mixins/toolkit/pension/pension';
import { getFormattedMemberName } from '@/app/utils/utils';
import calculateInitialPayments from '@/app/utils/toolkit/calculators/pensionMinPayment';

export default {
  name: 'schedule',
  mixins: [toolkit, pension],
  computed: {
    fund() {
      return this.$store.getters['fund/getFund'];
    },
    fundName() {
      return this.fund.details.name;
    },
    isAccumulationValid() {
      return (
        this.accumulation !== null && this.accumulation.components !== null
      );
    },
    isPensionValid() {
      return this.pension !== null && this.pension.amount !== null;
    },
    isBeneficiaryValid() {
      return (
        this.pension !== null
          && this.pension.reversionaryBeneficiary.hasReversionaryBeneficiary
          !== undefined
      );
    },
    partTwoMiniBalance() {
      if (this.isAccumulationValid) {
        if (this.accumulation.components.taxable.percent === 1) {
          return '100% taxable component';
        }
        return '100% tax free component';
      }
      return '';
    },
    accumulationTaxable() {
      if (this.isAccumulationValid) {
        return this.accumulation.components.taxable.value;
      }
      return 0;
    },
    accumulationNonTaxable() {
      if (this.isAccumulationValid) {
        return this.accumulation.components.nonTaxable.value;
      }
      return 0;
    },
    accumulationTotal() {
      if (this.isAccumulationValid) {
        // eslint-disable-next-line max-len
        return (
          this.accumulation.components.taxable.value + this.accumulation.components.nonTaxable.value
        );
      }
      return 0;
    },
    pensionTaxable() {
      if (this.isAccumulationValid && this.isPensionValid) {
        return (
          this.calculatedPensionAmount * this.accumulation.components.taxable.percent
        );
      }
      return 0;
    },
    pensionNonTaxable() {
      if (this.isAccumulationValid && this.isPensionValid) {
        return (
          this.calculatedPensionAmount * this.accumulation.components.nonTaxable.percent
        );
      }
      return 0;
    },
    pensionTotal() {
      if (this.isAccumulationValid && this.isPensionValid) {
        return this.calculatedPensionAmount;
      }
      return 0;
    },
    pensionTaxFreePercentage() {
      if (this.isAccumulationValid && this.isPensionValid) {
        return this.accumulation.components.nonTaxable.percent;
      }
      return 0;
    },
    preservedAmount() {
      if (this.isAccumulationValid && this.isAccumulationValid) {
        if (this.isTris) {
          return (
            this.calculatedPensionAmount - this.pension.unrestrictedNonPreserve
          );
        }
      }
      return 0;
    },
    unrestrictedAmount() {
      if (this.isAccumulationValid && this.isPensionValid) {
        if (this.isTris) {
          return this.pension.unrestrictedNonPreserve;
        }
        return this.calculatedPensionAmount;
      }
      return 0;
    },
    conditionOfRelease() {
      if (this.getItem.requirements.member.age >= 65) {
        return 'Reached age 65';
      }
      const releases = {
        'The pension will be a transition to retirement pension': 'Reached preservation age and wish to commence a transition to retirement income stream',
        'Permanently retired': 'Reached preservation age and permanently retired.',
        'Ended a paid work arrangement after turning 60': 'Terminated an employment relationship after age 60.',
        'Suffering from a terminal medical condition': 'Terminal medical condition',
        'Permanently incapacitated': 'Permanent incapacity',
        other: `${this.getItem.requirements.eligibility.otherReason}`,
      };

      return releases[this.getItem.requirements.eligibility.reasons];
    },
    reversionaryWording() {
      if (!this.isBeneficiaryValid) {
        return '';
      }
      if (
        this.pension.reversionaryBeneficiary.hasReversionaryBeneficiary === 'No'
      ) {
        return 'No.';
      }
      return 'Yes, providing the nominated beneficiary is permitted by superannuation law to receive the reversionary pension at the time.';
    },
    trustDeedWording() {
      if (!this.isBeneficiaryValid) {
        return '';
      }
      if (this.pension.reversionaryBeneficiary.hasReversionaryBeneficiary === 'No') {
        return 'N/A';
      }
      if (this.pension.reversionaryBeneficiary.hasTrustDeed === 'Unknown') {
        return 'Not known at this stage.';
      }

      return this.pension.reversionaryBeneficiary.hasTrustDeed === 'Yes'
        ? 'No.'
        : 'Yes.';
    },
    beneficiaryPerson() {
      if (this.isBeneficiaryValid) {
        if (this.pension.reversionaryBeneficiary.hasReversionaryBeneficiary === 'No') {
          return 'N/A';
        }
        return getFormattedMemberName(
          this.pension.reversionaryBeneficiary.beneficiaryDetails,
        );
      }

      return 'N/A';
    },
    beneficiaryRelationship() {
      if (this.isBeneficiaryValid) {
        if (this.pension.reversionaryBeneficiary.hasReversionaryBeneficiary === 'No') {
          return 'N/A';
        }
        return this.pension.reversionaryBeneficiary.beneficiaryDetails.relationship === 'Other allowable beneficiary'
          ? this.pension.reversionaryBeneficiary.beneficiaryDetails.otherRelationship
          : this.pension.reversionaryBeneficiary.beneficiaryDetails.relationship;
      }

      return 'N/A';
    },
    initialPayments() {
      if (this.isAccumulationValid && this.isPensionValid) {
        const payments = calculateInitialPayments(
          this.getItem.requirements.member.age,
          new Date(this.getItem.requirements.startDate),
          this.calculatedPensionAmount,
          this.isTris,
        );
        return {
          minimum: payments.minimum,
          maximum: payments.maximum,
        };
      }

      return {
        minimum: 0,
        maximum: 0,
      };
    },
  },
};
